import { generatePath } from 'react-router-dom';

import type { InvestmentBoardEntryDetailDto, InvestmentBoardTimerDto } from '@/api/pipelines/types';
import { PagePaths } from '@/routes/page-paths';

import type { PipelineCardDetail, PipelineTone } from '../../common/types';
import { isPresent } from '../../common/utils/mapping';
import {
  getInvestmentFundLabel,
  mapInvestmentEntryToPipelineCard,
  mapInvestmentTimerStatusToTone,
} from './map-card';
import { getInvestmentStageBadge, getInvestmentStageLabel } from './stage-presentation';

export const mapInvestmentEntryToPipelineCardDetail = (
  detail: InvestmentBoardEntryDetailDto,
): PipelineCardDetail => {
  const card = mapInvestmentEntryToPipelineCard(detail);

  return {
    ...card,
    badges: [...card.badges, getInvestmentStageBadge(detail.stage)],
    headerActions: getInvestmentDetailActions(detail),
    sections: [
      {
        key: 'summary',
        title: 'Summary',
        rows: [
          { key: 'stage', label: 'Stage', value: getInvestmentStageLabel(detail.stage) },
          { key: 'fund', label: 'Fund', value: getInvestmentFundLabel(detail.fund) },
          { key: 'fee', label: 'Fee', value: getInvestmentAmountLabel(detail.amount) },
          {
            key: 'feeEligibility',
            label: 'Fee eligibility',
            value: detail.feeEligibility.visible ? detail.feeEligibility.label : null,
            tone: getFeeEligibilityTone(detail.feeEligibility.status),
          },
          {
            key: 'window',
            label: 'Window',
            value: getTimerWindowLabel(detail.timerDetails[0] ?? null),
            tone: getTimerRowTone(detail.timerDetails[0] ?? null),
          },
        ],
      },
    ],
    timeline: detail.timeline,
  };
};

type PipelineDetailAction = PipelineCardDetail['headerActions'][number];

const getInvestmentDetailActions = (
  detail: InvestmentBoardEntryDetailDto,
): PipelineDetailAction[] =>
  [
    getInvestmentFundProfileAction(detail),
    detail.links.investorProfilePath
      ? {
          key: 'investorProfile',
          label: 'Profile',
          href: detail.links.investorProfilePath,
          placement: 'inlineMeta' as const,
        }
      : null,
    detail.links.introducerProfilePath
      ? {
          key: 'introducerProfile',
          label: 'Introducer Profile',
          href: detail.links.introducerProfilePath,
        }
      : null,
    detail.links.communicationPath
      ? {
          key: 'messageInvestor',
          label: 'Message Investor',
          href: detail.links.communicationPath,
          placement: 'primary' as const,
        }
      : null,
  ].filter(isPresent);

const getInvestmentFundProfileAction = (
  detail: InvestmentBoardEntryDetailDto,
): PipelineDetailAction | null => {
  const href =
    detail.links.fundProfilePath ??
    (detail.fund ? generatePath(PagePaths.fundDashboardId, { id: detail.fund.id }) : null);

  if (!href) return null;

  return {
    key: 'fundProfile',
    label: 'View Fund',
    href,
    placement: 'header',
  };
};

const getTimerRowTone = (timer: InvestmentBoardTimerDto | null): PipelineTone | undefined => {
  if (!timer) return undefined;

  const tone = mapInvestmentTimerStatusToTone(timer.status);
  return tone === 'yellow' ? 'amber' : tone;
};

const getTimerWindowLabel = (timer: InvestmentBoardTimerDto | null): string | null => {
  if (!timer) return null;

  return `${timer.daysRemaining} days remaining`;
};

const getInvestmentAmountLabel = (
  amount: InvestmentBoardEntryDetailDto['amount'],
): string | null => {
  if (amount.usd && amount.units) return `${amount.units} units / ${amount.usd} USD`;
  if (amount.usd) return `${amount.usd} USD`;
  if (amount.units) return `${amount.units} units`;
  return null;
};

const getFeeEligibilityTone = (
  status: InvestmentBoardEntryDetailDto['feeEligibility']['status'],
): PipelineTone | undefined => {
  if (status === 'Eligible') return 'green';
  if (status === 'NotEligible' || status === 'Expired') return 'red';
  if (status === 'Pending') return 'amber';
  return undefined;
};
